import React, { useState } from 'react';
import { CompleteKundali, Language, DashaPeriod, PlanetPosition } from '../../types/kundali';
import { 
  FileCheck2, 
  BookOpen, 
  Sparkles, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp, 
  ShieldAlert, 
  Layers, 
  Cpu, 
  CheckCircle2,
  HelpCircle,
  Clock
} from 'lucide-react';
import { calculateLivePlanetaryEphemeris } from '../../utils/vedicEngine';

interface EvidenceProvenanceDrawerProps {
  kundali: CompleteKundali;
  lang?: Language;
  activeMD: DashaPeriod | null;
  activeAD: DashaPeriod | null;
  activePDPlanet?: string | null;
  targetAnalysisDateTime: Date;
}

export const EvidenceProvenanceDrawer: React.FC<EvidenceProvenanceDrawerProps> = ({
  kundali,
  lang = 'en',
  activeMD,
  activeAD,
  activePDPlanet,
  targetAnalysisDateTime
}) => {
  const isHi = lang === 'hi';
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [activeSection, setActiveSection] = useState<'fact' | 'rule' | 'interpretation' | 'inference' | 'speculation' | 'uncertainty'>('fact');

  const { birthDetails, planets, houses } = kundali;
  const panchangData = (kundali as any).panchanga || (kundali as any).panchang;
  const moonPlanet = planets.find(p => p.name === 'Moon');
  const btrStatus = birthDetails?.btrStatus || 'unverified';
  const isBtrVerified = btrStatus === 'rectified' || btrStatus === 'verified';

  // Live ephemeris at analytical moment
  const liveEphemeris = calculateLivePlanetaryEphemeris(targetAnalysisDateTime);
  const jupiterTransit = liveEphemeris.find(p => p.planet === 'Jupiter');
  const saturnTransit = liveEphemeris.find(p => p.planet === 'Saturn');

  const mdPlanetObj = planets.find(p => p.name === activeMD?.planet);
  const adPlanetObj = planets.find(p => p.name === activeAD?.planet);

  return (
    <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl shadow-sm overflow-hidden mb-5">
      {/* Header bar */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="bg-[#174C4B]/5 px-4 py-3 border-b border-[#B58A4A]/20 flex items-center justify-between cursor-pointer hover:bg-[#174C4B]/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileCheck2 className="w-4 h-4 text-[#B58A4A]" />
          <h2 className="font-serif font-bold text-sm text-[#174C4B] tracking-wide">
            {isHi ? 'प्रमाण व स्रोत विवरण (Evidence & Provenance)' : 'Evidence & Provenance Traceability'}
          </h2>
          <span className="text-2xs font-semibold px-2 py-0.5 rounded-full bg-[#174C4B]/10 text-[#174C4B]">
            6-Part Epistemic Taxonomy
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button className="text-stone-500 hover:text-stone-800 transition-colors">
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="p-4 space-y-4">
          {/* Section Navigation Tabs: 6 Strict Epistemic Categories */}
          <div className="flex items-center gap-1.5 border-b border-stone-200 pb-2 overflow-x-auto">
            <button
              onClick={() => setActiveSection('fact')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-semibold whitespace-nowrap transition-all ${
                activeSection === 'fact'
                  ? 'bg-[#174C4B] text-white shadow-2xs'
                  : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
              }`}
            >
              <Cpu className="w-3 h-3" />
              <span>1. {isHi ? 'तथ्य' : 'Calculated Fact'}</span>
            </button>

            <button
              onClick={() => setActiveSection('rule')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-semibold whitespace-nowrap transition-all ${
                activeSection === 'rule'
                  ? 'bg-[#174C4B] text-white shadow-2xs'
                  : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
              }`}
            >
              <BookOpen className="w-3 h-3" />
              <span>2. {isHi ? 'नियम' : 'Classical Rule'}</span>
            </button>

            <button
              onClick={() => setActiveSection('interpretation')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-semibold whitespace-nowrap transition-all ${
                activeSection === 'interpretation'
                  ? 'bg-[#174C4B] text-white shadow-2xs'
                  : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
              }`}
            >
              <Sparkles className="w-3 h-3" />
              <span>3. {isHi ? 'फलित' : 'Interpretation'}</span>
            </button>

            <button
              onClick={() => setActiveSection('inference')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-semibold whitespace-nowrap transition-all ${
                activeSection === 'inference'
                  ? 'bg-[#174C4B] text-white shadow-2xs'
                  : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
              }`}
            >
              <Layers className="w-3 h-3" />
              <span>4. {isHi ? 'अनुमान' : 'Inference'}</span>
            </button>

            <button
              onClick={() => setActiveSection('speculation')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-semibold whitespace-nowrap transition-all ${
                activeSection === 'speculation'
                  ? 'bg-[#174C4B] text-white shadow-2xs'
                  : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
              }`}
            >
              <HelpCircle className="w-3 h-3" />
              <span>5. {isHi ? 'अटकल' : 'Speculation'}</span>
            </button>

            <button
              onClick={() => setActiveSection('uncertainty')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-semibold whitespace-nowrap transition-all ${
                activeSection === 'uncertainty'
                  ? 'bg-[#174C4B] text-white shadow-2xs'
                  : 'bg-white text-stone-600 hover:text-stone-900 border border-stone-200'
              }`}
            >
              <AlertTriangle className="w-3 h-3 text-amber-400" />
              <span>6. {isHi ? 'अनिश्चितता' : 'Uncertainty'}</span>
            </button>
          </div>

          {/* Section 1: Calculated Fact */}
          {activeSection === 'fact' && (
            <div className="bg-white border border-stone-200 rounded-xl p-3.5 text-2xs space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 bg-stone-50 p-2.5 rounded-lg border border-stone-200/80">
                  <span className="font-bold text-[#174C4B] uppercase tracking-wider block text-3xs">
                    Natal Birth Ephemeris (Calculated Fact)
                  </span>
                  <div><strong>Moon Sidereal Longitude:</strong> {moonPlanet?.longitude.toFixed(4)}° ({moonPlanet?.sign} {moonPlanet?.degreeInSign.toFixed(2)}°)</div>
                  <div><strong>Moon Nakshatra:</strong> {moonPlanet?.nakshatra} (Pada {moonPlanet?.nakshatraLord})</div>
                  <div><strong>Lahiri Ayanamsha:</strong> {panchangData?.ayanamshaFormatted || '23° 45\' 12"'}</div>
                  <div><strong>Birth Timestamp:</strong> {birthDetails?.date} {birthDetails?.time} ({birthDetails?.timezoneOffsetMinutes} mins)</div>
                </div>

                <div className="space-y-1.5 bg-stone-50 p-2.5 rounded-lg border border-stone-200/80">
                  <span className="font-bold text-[#174C4B] uppercase tracking-wider block text-3xs">
                    Active Dasha Hierarchy (Calculated Fact)
                  </span>
                  <div><strong>Mahadasha (MD):</strong> {activeMD?.planet} ({activeMD?.startDate} → {activeMD?.endDate})</div>
                  <div><strong>Antardasha (AD):</strong> {activeAD?.planet} ({activeAD?.startDate} → {activeAD?.endDate})</div>
                  <div><strong>Pratyantardasha (PD):</strong> {activePDPlanet || 'Calculated live from AD span'}</div>
                  <div><strong>MD Natal Placement:</strong> H{mdPlanetObj?.house || 1} in {mdPlanetObj?.sign || 'Aries'}</div>
                  <div><strong>AD Natal Placement:</strong> H{adPlanetObj?.house || 1} in {adPlanetObj?.sign || 'Aries'}</div>
                </div>
              </div>

              <div className="space-y-1.5 bg-stone-50 p-2.5 rounded-lg border border-stone-200/80">
                <span className="font-bold text-[#174C4B] uppercase tracking-wider block text-3xs">
                  Live Transit Coordinates at Shared Moment ({targetAnalysisDateTime.toISOString()})
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><strong>Jupiter (Guru):</strong> {jupiterTransit?.sign} {jupiterTransit?.formattedDegree} {jupiterTransit?.isRetrograde ? '(℞ Retrograde)' : ''}</div>
                  <div><strong>Saturn (Shani):</strong> {saturnTransit?.sign} {saturnTransit?.formattedDegree} {saturnTransit?.isRetrograde ? '(℞ Retrograde)' : ''}</div>
                </div>
              </div>
            </div>
          )}

          {/* Section 2: Classical Rule */}
          {activeSection === 'rule' && (
            <div className="bg-white border border-stone-200 rounded-xl p-3.5 text-2xs space-y-2.5 text-stone-700">
              <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                <strong className="text-[#174C4B] block mb-1">1. Parashari Drishti (Aspect) Law:</strong>
                <p>
                  All Grahas cast a full (100%) aspect on the 7th house from their placement. Special full aspects apply exclusively to:
                  Mars (4th, 8th), Jupiter/Rahu/Ketu (5th, 9th), and Saturn (3rd, 10th). Western Ptolemaic aspects are excluded.
                </p>
              </div>

              <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                <strong className="text-[#174C4B] block mb-1">2. Vimshottari 120-Year Proportional Standard:</strong>
                <p>
                  Calculated using BPHS 365.25 days/year solar year standard. The starting Mahadasha begins precisely at the birth date with the remaining Nakshatra balance.
                </p>
              </div>

              <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                <strong className="text-[#174C4B] block mb-1">3. Tri-Pillar Predictive Protocol (Promise → Dasha → Gochar):</strong>
                <p>
                  No life event occurs through transit alone. The Natal Chart establishes the root potential (Promise); the Vimshottari Dasha determines the active window of delivery (Activation); and Gochar transits trigger the exact timing.
                </p>
              </div>
            </div>
          )}

          {/* Section 3: Interpretation */}
          {activeSection === 'interpretation' && (
            <div className="bg-white border border-stone-200 rounded-xl p-3.5 text-2xs space-y-2 text-stone-700">
              <div className="flex items-center gap-2 text-xs font-bold text-[#174C4B]">
                <Sparkles className="w-4 h-4 text-[#B58A4A]" />
                <span>Analytical Commentary for Current Dasha Period ({activeMD?.planet} — {activeAD?.planet})</span>
              </div>
              <p className="leading-relaxed">
                The Mahadasha lord <strong>{activeMD?.planet}</strong> commands the overarching life climate, energizing its owned and occupied houses. The Antardasha lord <strong>{activeAD?.planet}</strong> operates as the functional executive. Their mutual geometric relationship from the natal Lagna sets the ease or friction of execution.
              </p>
              <div className="p-2.5 rounded-lg bg-amber-50/60 border border-amber-200 text-stone-800">
                <strong>Astrological Observation:</strong> Observe whether transiting Jupiter or Saturn aspects the active MD/AD house lords or natal placements during this timeframe to confirm transit triggers.
              </div>
            </div>
          )}

          {/* Section 4: Inference */}
          {activeSection === 'inference' && (
            <div className="bg-white border border-stone-200 rounded-xl p-3.5 text-2xs space-y-2 text-stone-700">
              <div className="flex items-center gap-2 text-xs font-bold text-[#174C4B]">
                <Layers className="w-4 h-4 text-[#B58A4A]" />
                <span>Inference Model (Traceable Astrological Deduction)</span>
              </div>
              <p className="leading-relaxed">
                <strong>Deduction:</strong> The co-presence of active Dasha periods ({activeMD?.planet}–{activeAD?.planet}) with ongoing Saturn and Jupiter transit aspects establishes an active developmental phase for houses ruled or occupied by these lords.
              </p>
              <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200 text-stone-600">
                <em>Note: Inferences are reasoned conclusions drawn from the intersection of calculated facts and classical rules, distinct from raw ephemeris data.</em>
              </div>
            </div>
          )}

          {/* Section 5: Speculation */}
          {activeSection === 'speculation' && (
            <div className="bg-white border border-stone-200 rounded-xl p-3.5 text-2xs space-y-2 text-stone-700">
              <div className="flex items-center gap-2 text-xs font-bold text-stone-600">
                <HelpCircle className="w-4 h-4 text-stone-400" />
                <span>Speculation Category</span>
              </div>
              <p className="leading-relaxed text-stone-600">
                No speculative models, probabilistic extrapolations, or unverified statistical guesswork are applied in this workspace. All outputs are strictly grounded in deterministic astronomical ephemeris and classical Parashari Jyotish principles.
              </p>
            </div>
          )}

          {/* Section 6: Uncertainty & Acharya Pause */}
          {activeSection === 'uncertainty' && (
            <div className="bg-white border border-stone-200 rounded-xl p-3.5 text-2xs space-y-2.5 text-stone-700">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-900 bg-amber-50 p-2 rounded-lg border border-amber-300">
                <ShieldAlert className="w-4 h-4 text-amber-600" />
                <span>Acharya Pause — Astrological Uncertainty Protocol</span>
              </div>

              <div className="space-y-2 text-stone-600">
                <p>
                  <strong>Birth-Time Sensitivity Principles:</strong>
                </p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><strong>Mahadasha & Antardasha:</strong> Remain broadly stable across minor birth-time variations.</li>
                  <li><strong>Micro-Dasha Timing (Sookshma & Prana):</strong> Highly sensitive to the exact recorded birth time. Sub-period transitions shift rapidly with small variances.</li>
                  <li><strong>Guideline:</strong> Where the native's birth time has not undergone calibration through life-event verification, fine-grained micro-Dasha predictions should be interpreted with scholarly caution.</li>
                </ul>

                {!isBtrVerified && (
                  <div className="p-2.5 rounded-lg bg-amber-50 text-amber-900 border border-amber-300 text-2xs">
                    <strong>Advisory:</strong> This native's birth time is currently uncalibrated. Exercise scholarly caution when interpreting sub-periods beyond Antardasha (AD).
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
